import { type FC, useEffect } from 'react';
import { useChatbotConfig, useCustomTypes } from './hooks';
import { SessionManager } from './utils/sessionManager';
import { AssistantModal } from './assistant-modal';
import { CHATBOT_CONSTANTS } from './constants';

/**
 * AppBootstrap component that manages pre-loading and error states
 */
export const AppBootstrap: FC = () => {
  // Initialize session and setup timeout monitoring on component mount
  useEffect(() => {
    SessionManager.getOrCreateSession();

    // Setup session timeout monitoring
    const timeoutInterval = setInterval(() => {
      if (SessionManager.isSessionExpired()) {
        SessionManager.resetSession();
        // Force refresh to reinitialize with new session
        window.location.reload();
      }
    }, CHATBOT_CONSTANTS.SESSION_TIMEOUT_CHECK_INTERVAL_MS);

    // Cleanup interval on unmount
    return () => {
      clearInterval(timeoutInterval);
    };
  }, []);

  // Execute both API queries in parallel with individual hooks
  const { config, error: configurationError } = useChatbotConfig();

  const { customTypes, error: customTypesError } = useCustomTypes();

  // Combine loading and error states
  const hasError = !!configurationError || !!customTypesError;
  const isReady = !!config && !!customTypes && !hasError;

  // Handle error conditions
  useEffect(() => {
    if (customTypesError) {
      console.error(
        '[AppBootstrap] Custom types API failed:',
        customTypesError,
      );
    }
    if (configurationError) {
      console.error(
        '[AppBootstrap] Configuration API failed:',
        configurationError,
      );
    }
  }, [customTypesError, configurationError]);

  // Don't render if any API call failed
  if (hasError) {
    console.error('[AppBootstrap] API errors detected, preventing render:', {
      customTypesError: customTypesError,
      configurationError: configurationError,
    });
    return null; // Prevents React app from rendering
  }

  // Don't render if data is missing (shouldn't happen if no errors)
  if (!isReady) {
    console.error('[AppBootstrap] Missing required data, preventing render:', {
      hasConfig: !!config,
      hasCustomTypes: !!customTypes,
    });
    return null; // Prevents React app from rendering
  }

  // All APIs loaded successfully, render the actual app
  return <AssistantModal />;
};
